import type { MaybePromise } from "#root/types";
import { debounce } from "#root/utility/helpers";
import { Invalid, type Validity } from "#root/utility/validators";
import type { ReactNode } from "react";
import { createContext, type FormHTMLAttributes, useCallback, useContext, useMemo, useState } from "react";

export type FormContextType = {
  disabled: boolean;
  valid: boolean;
  dirty: boolean;
  field: (name: string) => FieldDataType;
  mutateField: (name: string, func: (data: FieldDataType) => FieldDataType) => FieldDataType;
};

export type FieldDataType = {
  dirty: boolean;
  error?: string | null;
  value?: unknown;
  validity?: Validity;
  focused?: boolean;
  hint?: string;
};
export const FormContext = createContext<FormContextType | undefined>(undefined);

export function useForm() {
  return useContext(FormContext);
}

export type FormProps<RowType extends Record<string, unknown>> = Omit<FormHTMLAttributes<HTMLFormElement>, "onSubmit"> & {
  onChange?: (data: RowType) => void;
  data: Partial<RowType>;
  dirty?: boolean;
  disabled?: boolean;
  onSubmit: (data: RowType) => MaybePromise<void>;
  children: ReactNode;
};

export function Form<RowType extends Record<string, unknown>>({
                                                                onChange,
                                                                data,
                                                                dirty: initialDirty = false,
                                                                disabled: initialDisabled = false,
                                                                onSubmit,
                                                                children,
                                                                ...props
                                                              }: FormProps<RowType>) {
  const [disabled] = useState(initialDisabled);
  const [valid, setValid] = useState(false);
  const [dirty, setDirty] = useState(initialDirty);

  const fields = useMemo(() => {
    const fields: Record<string, FieldDataType> = {};
    for (const key of Object.keys(data || {})) {
      const value = data[key];
      //TODO This is used by the autocomplete component, but it should be implemented differently
      if (value && typeof value === "object" && "id" in value && value.id != null) {
        console.info("Found id in value", value, data);
        fields[key] = { dirty: initialDirty, error: null, value };
      } else {
        fields[key] = { dirty: initialDirty, error: null, value };
      }
    }

    return fields;
  }, [data, initialDirty]);

  const field = useCallback(
    (name: string) => {
      fields[name] ??= { dirty: false };
      return fields[name];
    },
    [fields],
  );

  const mergeData = useCallback(() => {
    let valid = true;
    let dirty = false;

    const data = {};
    for (const key of Object.keys(fields)) {
      const row = fields[key];
      data[key] = row.value;
      if (row.validity === Invalid) {
        valid = false;
      }

      if (row.dirty) {
        dirty = true;
      }
    }

    setValid(valid);
    setDirty(dirty);
    return data as RowType;
  }, [fields]);

  const handleChange = useMemo(
    () =>
      debounce(
        () => {
          const data = mergeData();
          if (onChange) {
            onChange(data);
          }
        },
        250,
        false,
      ),
    [onChange, mergeData],
  );

  const mutateField = useCallback(
    (name: string, func: (data: FieldDataType) => FieldDataType) => {
      const data = fields[name] || {};
      fields[name] = func(data);
      handleChange();
      return data;
    },
    [fields, handleChange],
  );

  const handleSubmit = useCallback(
    evnt => {
      evnt.preventDefault();
      if (!valid) {
        return;
      }

      if (disabled) {
        return;
      }

      const data: RowType = mergeData();
      return onSubmit(data);
    },
    [valid, disabled, mergeData, onSubmit],
  );

  const context = useMemo(
    () => ({
      disabled,
      valid,
      dirty,
      field,
      mutateField,
    }),
    [disabled, valid, dirty, field, mutateField],
  );

  // Console.log(fields);
  return (
    <form autoComplete="off" action="about:" onSubmit={handleSubmit} {...props}>
      <FormContext.Provider value={context}>{children}</FormContext.Provider>
    </form>
  );
}
